import { BucketIdentifier, S3File } from "@dune/types";
import { Progress, UploadOneFile } from "@dune/ui";
import { useEffect, useState } from "react";
import { usePostFileOnAWSS3 } from "../../api/mutations/files.mutations";
import {
	getPrivateFileUrl,
	useGetSignedPutUrlQuery,
} from "../../api/queries/files.queries";

type S3FileInputProps = {
	bucket: BucketIdentifier;
	onUploadSuccess: (s3File: S3File) => void;
	onPreview?: (s3File: S3File) => void;
	onRemove?: () => void;
	subText?: string;
	hidePreviewButton?: boolean;
	initialFile?: S3File;
};

export const S3FileInput = ({
	bucket,
	onUploadSuccess,
	onPreview,
	subText,
	hidePreviewButton = false,
	initialFile,
}: S3FileInputProps) => {
	const { isLoading: isLoadingPutUrl, refetch: refetchPutUrl } =
		useGetSignedPutUrlQuery(bucket);

	const { mutateAsync: postFileOnAwsS3, isPending: isUploadingToS3 } =
		usePostFileOnAWSS3();

	const [uploadedFile, setUploadedFile] = useState<S3File | null>(
		initialFile || null,
	);
	const [isProcessing, setIsProcessing] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [currentFileName, setCurrentFileName] = useState<string>("");

	useEffect(() => {
		const initializeFile = async () => {
			if (initialFile) {
				if (!initialFile.url && initialFile.key) {
					try {
						const signedUrl = await getPrivateFileUrl(initialFile.key);
						setUploadedFile({ ...initialFile, url: signedUrl });
					} catch (error) {
						console.error("Error getting initial file URL:", error);
						setUploadedFile(initialFile);
					}
				} else {
					setUploadedFile(initialFile);
				}
			} else {
				setUploadedFile(null);
			}
		};

		initializeFile();
	}, [initialFile]);

	const isUploading = isLoadingPutUrl || isUploadingToS3 || isProcessing;

	const handleFileChange = async (file: File | null) => {
		if (!file || isLoadingPutUrl) return;

		try {
			setIsProcessing(true);
			setCurrentFileName(file.name);
			setUploadProgress(0);

			setUploadProgress(25);
			const { data: signedPut } = await refetchPutUrl();

			if (!signedPut?.signedUrl || !signedPut?.key) {
				throw new Error("Impossible d'obtenir l'URL signée");
			}

			setUploadProgress(50);
			await postFileOnAwsS3({ file, url: signedPut.signedUrl });
			setUploadProgress(75);

			const signedGet = await getPrivateFileUrl(signedPut.key);
			setUploadProgress(100);

			const s3File: S3File = {
				bucket,
				key: signedPut.key,
				type: file.type,
				size: file.size,
				name: file.name,
				url: signedGet || "",
			};

			setUploadedFile(s3File);
			onUploadSuccess(s3File);
		} catch (error) {
			console.error("Upload error:", error);

			setUploadProgress(0);
			setCurrentFileName("");
		} finally {
			setIsProcessing(false);

			setTimeout(() => {
				setUploadProgress(0);
				setCurrentFileName("");
			}, 500);
		}
	};

	const handleShowFile = async () => {
		if (uploadedFile && onPreview) {
			onPreview({
				...uploadedFile,
			});
		}
	};

	// Si on est en train d'uploader, afficher la progress bar
	if (isUploading) {
		return (
			<div className="border-input hover:bg-accent/50 flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed p-6 transition-colors">
				<div className="w-full max-w-xs space-y-3">
					<div className="text-center">
						<div className="text-sm font-medium text-gray-900">
							Upload en cours...
						</div>
						<div className="text-sm text-gray-500">{currentFileName}</div>
					</div>
					<Progress value={uploadProgress} className="h-2" />
					<div className="text-center text-xs text-gray-400">
						{uploadProgress}%
					</div>
				</div>
			</div>
		);
	}

	return (
		<div>
			<UploadOneFile
				onFileChange={handleFileChange}
				onShowFile={handleShowFile}
				subText={subText}
				hidePreviewButton={hidePreviewButton}
				initialFile={
					uploadedFile
						? {
								name: uploadedFile.name,
								size: uploadedFile.size,
								type: uploadedFile.type,
								url: uploadedFile.url || "",
								id: uploadedFile.key,
							}
						: undefined
				}
			/>
		</div>
	);
};
